import { useEffect, useState } from 'react';
import { json, LoaderFunction, MetaFunction } from '@remix-run/node';
import { Form, Link, useLoaderData, useNavigation, useSearchParams } from '@remix-run/react';
import { NEWS_CATEGORIES, NEWS_CATEGORY_MAPPING } from '~/constants/news';
import { motion } from 'framer-motion';
import { formatDate } from '~/lib/utils';
import { Search, Calendar, Eye, TrendingUp, BookOpen, Clock } from 'lucide-react';
import { CategoryCard, CategoryType } from '~/components/ui/category-card';

// 뉴스 타입 정의
interface News {
  id: string;
  title: string;
  summary?: string;
  content: string;
  thumbnail_url?: string;
  category: string;
  view_count: number;
  created_at: string;
}

// 뉴스 카테고리 매핑 확장 타입
type NewsCategory = keyof typeof NEWS_CATEGORY_MAPPING;

// 배열로 변환한 NEWS_CATEGORIES 생성
const NEWS_CATEGORIES_ARRAY = Object.values(NEWS_CATEGORIES);

// 실제 DB 카테고리를 CategoryType으로 변환하는 함수
const mapDBCategoryToCategoryType = (category: string): CategoryType => {
  switch (category) {
    case 'firm_news':
      return 'news';
    case 'case_analysis':
      return 'case';
    case 'legal_trends':
      return 'trend';
    case 'legal_column':
      return 'column';
    default:
      return 'news';
  }
};

// 카테고리별 배경 색상 가져오기
const getCategoryBgColor = (category: string): string => {
  switch (category) {
    case 'news':
      return 'bg-indigo-100';
    case 'case':
      return 'bg-purple-100';
    case 'trend':
      return 'bg-amber-100';
    case 'column':
      return 'bg-rose-100';
    default:
      return 'bg-gray-100';
  }
};

// 카테고리별 텍스트 색상 가져오기
const getCategoryTextColor = (category: string): string => {
  switch (category) {
    case 'news':
      return 'text-indigo-700';
    case 'case':
      return 'text-purple-700';
    case 'trend':
      return 'text-amber-700';
    case 'column':
      return 'text-rose-700';
    default:
      return 'text-gray-700';
  }
};

// 카테고리별 태그 색상 가져오기
const getColorForCategory = (category: string): string => {
  switch (category) {
    case 'news':
      return 'bg-indigo-100 text-indigo-800';
    case 'case':
      return 'bg-purple-100 text-purple-800';
    case 'trend':
      return 'bg-amber-100 text-amber-800';
    case 'column':
      return 'bg-rose-100 text-rose-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// 카드 변형 선택 함수 수정
const getCategoryCardVariant = (category: string, index: number) => {
  // 메인 뉴스 목록은 'elegant' 스타일로 통일
  return 'elegant';
};

// 인기 뉴스용 카드 변형 선택 함수 추가
const getPopularNewsCardVariant = (category: string) => {
  // 인기 뉴스는 'initial' 스타일로 통일 (이니셜 표시)
  return 'initial';
};

// 콘텐츠에서 첫 100자 추출
const extractContentPreview = (content: string): string => {
  // HTML 태그 제거
  const plainText = content ? content.replace(/<[^>]+>/g, '') : '';
  return plainText.slice(0, 100);
};

// 카테고리별 한글 이니셜 매핑 추가
const getCategoryInitial = (category: string): string => {
  switch (category) {
    case 'news':
      return '내';
    case 'case':
      return '판';
    case 'trend':
      return '뉴';
    case 'column':
      return '칼';
    default:
      return '뉴';
  }
};

// 콘텐츠 길이에 따른 읽기 시간 예상 함수 수정
const getReadingTime = (content: string): string => {
  // HTML 태그 제거
  const plainText = content ? content.replace(/<[^>]+>/g, '') : '';
  // 한글 기준으로 분당 500자 읽는다고 가정 (더 빠르게 읽을 수 있다는 인상을 줌)
  const words = plainText.length;
  const minutes = Math.max(1, Math.ceil(words / 800));
  
  // 읽기 시간을 직접적으로 표시
  return `${minutes}분`;
};

export const meta: MetaFunction = () => {
  return [
    { title: '뉴스 - 법무법인 휘상' },
    { name: 'description', content: '법무법인 휘상의 소식과 법률 정보를 확인하세요.' },
  ];
};

export const loader: LoaderFunction = async ({ request }) => {
  try {
    const url = new URL(request.url);
    const category = url.searchParams.get('category') || 'all';
    const page = parseInt(url.searchParams.get('page') || '1', 10);
    const searchTerm = url.searchParams.get('search') || '';
    
    // 서버 측 모듈 동적 임포트 - 최적화를 위해 imports.all() 사용
    const newsActions = await import('~/actions/news/index.server');
    
    // 필요한 두 함수만 병렬로 호출
    const [newsList, popularNews] = await Promise.all([
      newsActions.getNewsList({ category, page, limit: 10, searchTerm }),
      newsActions.getCategoryLatestNews(),
    ]);
    
    return json({ 
      newsList, 
      popularNews,
      currentCategory: category,
      searchTerm
    }, {
      headers: {
        'Cache-Control': 'public, max-age=30, s-maxage=60'
      }
    });
  } catch (error) {
    console.error('뉴스 목록 로딩 오류:', error);
    // 오류 발생 시에도 기본 데이터로 응답하여 UI가 깨지지 않도록 함
    return json({ 
      newsList: { 
        items: [], 
        pagination: { 
          totalItems: 0, 
          totalPages: 1, 
          currentPage: 1, 
          limit: 10, 
          hasNextPage: false, 
          hasPrevPage: false 
        } 
      },
      popularNews: [],
      currentCategory: 'all',
      searchTerm: ''
    }, {
      headers: {
        'Cache-Control': 'no-store'
      }
    });
  }
};

// 뉴스 카드 애니메이션 변수
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

export default function NewsIndex() {
  const loaderData = useLoaderData<typeof loader>();
  const { newsList, popularNews, currentCategory, searchTerm } = loaderData;
  
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const isLoading = navigation.state === 'loading';
  
  // 현재 페이지 번호 및 페이지네이션 데이터 안전하게 처리
  const currentPage = newsList?.pagination?.currentPage || 1;
  const totalPages = newsList?.pagination?.totalPages || 1;
  const hasItems = newsList?.items?.length > 0;
  
  // 페이지 번호 그룹 (1-10, 11-20 등)
  const [pageGroup, setPageGroup] = useState(Math.ceil(currentPage / 10));
  
  // URL 파라미터 유지하면서 페이지만 변경하는 함수
  const getPageUrl = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    return `?${params.toString()}`;
  };
  
  // 카테고리 URL 생성 함수
  const getCategoryUrl = (category: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('category', category);
    params.delete('page'); // 카테고리 변경 시 페이지 초기화
    return `?${params.toString()}`;
  };
  
  // 페이지 그룹 계산
  useEffect(() => {
    setPageGroup(Math.ceil(currentPage / 10));
  }, [currentPage]);
  
  // 페이지 범위 계산 (예: 1-10, 11-20)
  const startPage = (pageGroup - 1) * 10 + 1;
  const endPage = Math.min(pageGroup * 10, totalPages);
  
  // 페이지 번호 배열 생성
  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }
  
  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      {/* 헤더 섹션 - 깔끔하고 세련된 디자인으로 수정 */}
      <div className="relative bg-white py-16 border-b border-gray-100 shadow-sm">
        <div className="absolute inset-0 bg-[#fafafa] opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-4 tracking-tight"
          >
            뉴스 및 법률 정보
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-lg text-gray-600 max-w-2xl font-light"
          >
            법무법인 휘상의 소식과 다양한 법률 정보를 확인하세요
          </motion.p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* 카테고리 탭 */}
        <div className="mb-8 overflow-x-auto scrollbar-hide">
          <div className="flex space-x-2 min-w-max pb-2">
            <Link
              to={getCategoryUrl('all')}
              prefetch="intent"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                currentCategory === 'all'
                  ? 'bg-gray-800 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              전체
            </Link>
            
            {/* 카테고리 목록 */}
            {NEWS_CATEGORIES_ARRAY.map((category) => (
              <Link
                key={category}
                to={getCategoryUrl(category)}
                prefetch="intent"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  currentCategory === category
                    ? 'bg-gray-800 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {NEWS_CATEGORY_MAPPING[category as NewsCategory]}
              </Link>
            ))}
          </div>
        </div>
        
        {/* 검색 폼 - 원래 디자인으로 복원 */}
        <div className="mb-8">
          <Form method="get" className="flex gap-2 max-w-md">
            <input type="hidden" name="category" value={currentCategory} />
            <div className="flex-1 relative">
              <input
                type="text"
                name="search"
                placeholder="검색어를 입력하세요"
                defaultValue={searchTerm}
                className="w-full py-2 px-4 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-gray-800 transition-all"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <Search size={18} />
              </span>
            </div>
            <button
              type="submit"
              className="bg-white text-gray-800 py-2 px-6 rounded-lg border-2 border-gray-800 hover:bg-gray-50 transition-colors duration-200 flex items-center gap-2 font-medium"
            >
              검색
            </button>
          </Form>
        </div>
        
        {/* 인기 뉴스 및 메인 콘텐츠 그리드 - 원래 디자인으로 복원 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 메인 뉴스 목록 */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">최신 게시글</h2>

            {/* 로딩 상태 */}
            {isLoading && (
              <div className="py-12 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-800 mb-4"></div>
                <p className="text-gray-600">로딩 중...</p>
              </div>
            )}

            {/* 검색 결과 없음 */}
            {!isLoading && !hasItems && (
              <div className="py-12 flex flex-col items-center justify-center bg-white rounded-xl shadow-sm">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                  <Search size={24} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-1">검색 결과가 없습니다</h3>
                <p className="text-gray-600 text-center max-w-md">
                  {searchTerm 
                    ? `'${searchTerm}'에 대한 검색 결과가 없습니다. 다른 검색어를 시도해보세요.`
                    : '해당 카테고리에 게시글이 없습니다.'
                  }
                </p>
              </div>
            )}

            {/* 카드 디자인 */}
            {!isLoading && hasItems && newsList.items.map((news: News, i: number) => (
              <motion.div
                key={news.id}
                custom={i}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
              >
                <Link to={`/news/${news.id}`} className="flex flex-col md:flex-row">
                  <div className="md:w-1/3 overflow-hidden">
                    <div className="h-48 md:h-full">
                      <CategoryCard 
                        category={mapDBCategoryToCategoryType(news.category)}
                        height="h-full"
                        variant={getCategoryCardVariant(mapDBCategoryToCategoryType(news.category), i) as any}
                        contentPreview={extractContentPreview(news.content)}
                      />
                    </div>
                  </div>
                  <div className="p-4 md:p-6 md:w-2/3 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center mb-2">
                        <span className={`text-xs rounded-full ${getColorForCategory(mapDBCategoryToCategoryType(news.category))} px-2 py-1 font-medium`}>
                          {NEWS_CATEGORY_MAPPING[news.category]}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 leading-snug hover:text-gray-700 transition-colors">
                        {news.title}
                      </h3>
                      {news.summary && (
                        <p className="text-gray-600 line-clamp-2 mb-4">{news.summary}</p>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-500 mt-2">
                      <span className="flex items-center mr-4">
                        <Calendar size={14} className="mr-1" />
                        {formatDate(news.created_at)}
                      </span>
                      <span className="flex items-center">
                        <Clock size={14} className="mr-1" />
                        {getReadingTime(news.content)}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}

            {/* 페이지네이션 */}
            {!isLoading && hasItems && totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <div className="flex items-center space-x-1">
                  {/* 이전 페이지 그룹 버튼 */}
                  {pageGroup > 1 && (
                    <Link
                      to={getPageUrl(startPage - 1)}
                      prefetch="intent"
                      className="px-3 py-2 rounded-md bg-white text-gray-700 hover:bg-gray-100"
                    >
                      &laquo;
                    </Link>
                  )}
                  
                  {/* 페이지 번호 */}
                  {pageNumbers.map((page) => (
                    <Link
                      key={page}
                      to={getPageUrl(page)}
                      prefetch="intent"
                      className={`px-3 py-2 rounded-md text-sm ${
                        page === currentPage
                          ? 'bg-gray-800 text-white font-medium'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </Link>
                  ))}
                  
                  {/* 다음 페이지 그룹 버튼 */}
                  {pageGroup * 10 < totalPages && (
                    <Link
                      to={getPageUrl(endPage + 1)}
                      prefetch="intent"
                      className="px-3 py-2 rounded-md bg-white text-gray-700 hover:bg-gray-100"
                    >
                      &raquo;
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* 사이드바 */}
          <div>
            {/* 인기 뉴스 */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
              <div className="border-b p-4 bg-gray-50">
                <div className="flex items-center">
                  <TrendingUp size={18} className="text-gray-700 mr-2" />
                  <h3 className="text-lg font-bold text-gray-800">카테고리별 최신 뉴스</h3>
                </div>
              </div>
              <div className="p-4">
                {popularNews.map((news: News, index: number) => (
                  <Link
                    key={news.id}
                    to={`/news/${news.id}`}
                    prefetch="intent"
                    className="flex items-start gap-3 group p-3 rounded-lg hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                  >
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-lg overflow-hidden">
                        {/* 파스텔톤 썸네일 디자인 */}
                        <div className={`w-full h-full flex items-center justify-center ${getCategoryBgColor(mapDBCategoryToCategoryType(news.category))}`}>
                          <span className={`text-4xl font-bold ${getCategoryTextColor(mapDBCategoryToCategoryType(news.category))} flex items-center justify-center w-full h-full opacity-90`}>
                            {getCategoryInitial(mapDBCategoryToCategoryType(news.category))}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <span className={`text-xs rounded-full ${getColorForCategory(mapDBCategoryToCategoryType(news.category))} px-2 py-0.5 mb-1 inline-block`}>
                        {NEWS_CATEGORY_MAPPING[news.category]}
                      </span>
                      <h4 className="text-gray-900 font-bold line-clamp-2 group-hover:text-gray-700 transition-colors text-base">
                        {news.title}
                      </h4>
                      <div className="flex items-center mt-1 text-xs text-gray-500">
                        <span className="flex items-center mr-2">
                          <Calendar size={12} className="mr-1" />
                          {formatDate(news.created_at)}
                        </span>
                        <span className="flex items-center">
                          <Clock size={12} className="mr-1" />
                          {getReadingTime(news.content)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            
            {/* 나머지 사이드바 컨텐츠 */}
          </div>
        </div>
      </div>
    </div>
  );
} 